import { useCallback, useEffect, useRef } from 'react';

export type DebouncedCallback<Args extends unknown[]> = ((...args: Args) => void) & {
  /** Clears any pending timeout and drops the pending args without invoking the callback. */
  cancel: () => void;
};

/**
 * Returns a stable debounced wrapper around `callback`. A burst of calls within `delay` ms
 * collapses into a single trailing invocation, using the most recently passed `callback`
 * and arguments — so callers don't need to memoize `callback` themselves.
 *
 * A pending call is flushed (invoked immediately), not dropped, on unmount — this is meant
 * for autosave-style callers (e.g. persisting a draft as the user types) where losing the
 * last edit because the component unmounted inside the debounce window would silently
 * discard real input.
 *
 * The returned function also exposes `cancel()`, which clears a pending timeout and drops
 * the pending args without invoking the callback — for a caller whose own authoritative
 * action (e.g. a "Complete"/"Save" click) needs to supersede a queued call rather than race
 * it, including suppressing the unmount-flush above for that now-superseded call.
 */
export function useDebouncedCallback<Args extends unknown[]>(
  callback: (...args: Args) => void,
  delay: number,
): DebouncedCallback<Args> {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingArgsRef = useRef<Args | null>(null);

  useEffect(
    () => () => {
      if (!timeoutRef.current) return;
      clearTimeout(timeoutRef.current);
      if (pendingArgsRef.current) callbackRef.current(...pendingArgsRef.current);
    },
    [],
  );

  const debounced = useCallback(
    (...args: Args) => {
      pendingArgsRef.current = args;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        pendingArgsRef.current = null;
        callbackRef.current(...args);
      }, delay);
    },
    [delay],
  );

  const cancel = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    pendingArgsRef.current = null;
  }, []);

  return Object.assign(debounced, { cancel });
}
